import React from 'react'
import { useTranslation } from 'react-i18next'

import { useQuery, gql } from '@apollo/client'
import classNames from 'classnames'
import { ExclamationIcon } from '@heroicons/react/solid'
import { PageHeader } from '../components/page-header'
import { PageBody } from '../components/page-body'
import Loader from '../components/loader'

import useGetCategoryName from '../hooks/useGetCategoryName'
import useGetSystemName from '../hooks/useGetSystemName'

const QUERY = gql`
  query Vendors {
    appAdminCompanies {
      id
      company
      accessRights
      userName
      systemsV2 {
        id
        defaultCategory
        ratingBoost
        systemSiteData {
          id
          systemName
          customerType
          locale
        }
      }
      buyingIntentBoost
      buyingIntentBoostCategories
      pipedriveId
    }
  }
`

function Vendor({ vendor }) {
  const { t } = useTranslation()
  const { getCategoryName } = useGetCategoryName()
  const { getSystemName } = useGetSystemName()
  const hasPipedriveId = vendor.pipedriveId

  let icon = null
  if (!hasPipedriveId) {
    icon = (
      <ExclamationIcon
        className="h-10 w-10 rounded-full text-yellow-400"
        aria-hidden="true"
      />
    )
  }

  const outboundHref = `/admin-vendor-outbounds/${vendor.id}`

  return (
    <tr key={vendor.company}>
      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
        <div className="flex items-center">
          {icon && <div className="h-10 w-10 flex-shrink-0">{icon}</div>}
          <div className={classNames({ 'ml-4': !hasPipedriveId })}>
            <div className="font-medium text-gray-900">{vendor.company}</div>
            <div className="text-xs text-gray-500">PID: {vendor.pipedriveId}</div>
            <div className="text-xs text-gray-500">BWID: {vendor.id}</div>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <div className="text-gray-900">
          {vendor.accessRights.map((right) => {
            return <div key={right}>{right}</div>
          })}
        </div>
        <div className="border-t-4 text-gray-500">
          BI-Boost:{' '}
          {vendor.buyingIntentBoost ? (
            <span style={{ fontWeight: 700 }}>{t('content:yes')}</span>
          ) : (
            t('content:no')
          )}
          <br />
          <ul style={{ fontWeight: 700 }}>
            {vendor.buyingIntentBoostCategories.map((cat, index) => (
              <li key={index}>{getCategoryName(cat, true)}</li>
            ))}
          </ul>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <div className="text-gray-500">
          {vendor.userName ? (
            <span style={{ fontWeight: 700 }}>{t('content:yes')}</span>
          ) : (
            t('content:no')
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {vendor.systemsV2?.map((system) => {
          return system?.systemSiteData.map((siteData) => {
            return (
              <div key={system}>
                {t(`shared:localeToCountry.${siteData.locale}`)} - {getSystemName(system)}{' '}
                - {siteData.customerType}
              </div>
            )
          })
        })}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {vendor.systemsV2?.map((system) => {
          return (
            <div key={system.id} style={{ display: 'flex', whiteSpace: 'pre' }}>
              {getSystemName(system)} -{' '}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getCategoryName(system.defaultCategory, true)}
                <span className="text-xs pl-1">
                  {system.systemSiteData?.map((siteData) => {
                    return (
                      <React.Fragment key={siteData.id}>
                        {' '}
                        {t(`shared:localeToCountry.${siteData.locale}`)} -{' '}
                        {siteData.customerType}
                      </React.Fragment>
                    )
                  })}
                </span>
              </span>
              {system?.ratingBoost !== null && (
                <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                  <svg
                    className="-ml-0.5 mr-1.5 h-2 w-2 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 8 8"
                  >
                    <circle cx="4" cy="4" r="3" />
                  </svg>
                  R: {system.ratingBoost}
                </span>
              )}
            </div>
          )
        })}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <a href={outboundHref}>{t('content:outbounds')}</a>
      </td>
    </tr>
  )
}

function AppVendors({ appVendors }) {
  const { t } = useTranslation()

  if (!appVendors?.length) {
    return <div>{t('content:warning.noData')}</div>
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      {t('content:company')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('content:privileges')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('content:zapier')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('content:markets')}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t('content:system')}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {appVendors
                    .slice()
                    .sort((a, b) => {
                      const nameA = a.company.toUpperCase()
                      const nameB = b.company.toUpperCase()
                      if (nameA < nameB) {
                        return -1
                      }
                      if (nameA > nameB) {
                        return 1
                      }

                      return 0
                    })
                    .map((vendor) => {
                      return <Vendor key={vendor.id} vendor={vendor} />
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function AdminAllVendorsPage() {
  const { t } = useTranslation('adminPages')
  const { data, loading, error } = useQuery(QUERY)

  if (error) {
    console.error(error)
  }

  const appVendors = data?.appAdminCompanies

  return (
    <>
      <PageHeader pageName={t('adminAllVendors.header.title')} />

      <PageBody>
        {error &&
          error.graphQLErrors.map(({ message }, i) => <span key={i}>{message}</span>)}
        {loading && (
          <div className="text-center">
            <Loader />
          </div>
        )}
        <div>{!loading && <AppVendors appVendors={appVendors} />}</div>
      </PageBody>
    </>
  )
}

export default AdminAllVendorsPage
